import { precisionFactor } from 'composables/useOcrProducts';

export const persistOcr = (data, params) => {
    const factor = precisionFactor(params.decimals ?? 2);

    const parseAmount = (value) => {
        if (value === null || value === undefined || value === '') {
            return null;
        }
        const parsed = parseFloat(String(value).replace(',', '.'));
        return Number.isNaN(parsed) ? null : parsed;
    };

    const parseQuantity = (quantity) => {
        const parsed = parseFloat(quantity);
        return Number.isNaN(parsed) ? 1 : parsed;
    };

    const toIntOrNull = (value) => {
        if (value === null || value === undefined || value === '') {
            return null;
        }
        const num = Number(value);
        return Number.isNaN(num) ? null : Math.trunc(num);
    };

    const currencyId = params.currencies.find((currency) => currency.name === data.currency)?.id;

    const hasTaxOverrides =
        data.lineMode === 'tax' ||
        data.products.some((product) => !_.isNil(product.taxAmountOverride));

    const mapProductRow = (product) => {
        const amountExcl = parseAmount(product.priceWithoutTax);
        const quantity = parseQuantity(product.quantity);
        const taxRate = params.taxRates?.find((item) => item.id === toIntOrNull(product.taxRateId));
        const rate = taxRate ? Number(taxRate.rate) : 0;

        let taxAmount = parseAmount(product.taxAmountOverride);
        if (taxAmount === null && amountExcl !== null) {
            const totalExclInCents = Math.round(amountExcl * quantity * factor);
            taxAmount = Math.round((totalExclInCents * rate) / 100) / factor;
        }

        let amountIncl = parseAmount(product.amountIncludingTaxOverride);
        if (amountIncl !== null) {
            amountIncl = Math.round((amountIncl / quantity) * factor) / factor;
        } else if (amountExcl !== null) {
            const exclInCents = Math.round(amountExcl * factor);
            const unitTaxInCents = Math.round((exclInCents * rate) / 100);
            amountIncl = (exclInCents + unitTaxInCents) / factor;
        }

        return {
            description: product.description,
            tax_rate_id: toIntOrNull(product.taxRateId),
            tax_amount: taxAmount,
            accounting_code_id: toIntOrNull(product.accountingCode),
            amount_excl: amountExcl,
            amount_incl: amountIncl,
            quantity,
            unit: toIntOrNull(product.unit),
            catalogue_link: toIntOrNull(product.catalogueLink?.id),
            analytic_code: product.analyticCode || null,
        };
    };

    const mapTaxLineRow = (taxLine) => {
        const amountExcl = parseAmount(taxLine.amountExcludingTax);
        const amountIncl = parseAmount(taxLine.amountIncludingTax);
        const taxAmount =
            amountExcl !== null && amountIncl !== null
                ? (Math.round(amountIncl * factor) - Math.round(amountExcl * factor)) / factor
                : null;

        return {
            description: taxLine.description,
            tax_rate_id: toIntOrNull(taxLine.taxRateId),
            tax_amount: taxAmount,
            accounting_code_id: toIntOrNull(taxLine.accountingCode),
            amount_excl: amountExcl,
            amount_incl: amountIncl,
            quantity: 1,
            unit: toIntOrNull(taxLine.unit),
            catalogue_link: null,
            analytic_code: taxLine.analyticCode || null,
        };
    };

    const rows =
        data.lineMode === 'tax'
            ? (data.taxLines || []).map(mapTaxLineRow)
            : data.products.map(mapProductRow);

    return {
        has_tax_overrides: hasTaxOverrides,
        supplier_id: toIntOrNull(data.supplier),
        date: data.documentDate.split(' ')[0],
        due_date: data.dueDate ? data.dueDate.split(' ')[0] : null,
        ident: data.externalDocumentName,
        currency_id: toIntOrNull(currencyId),
        step: data.step,
        note: data.note,
        analytic_code: data.analyticCode || null,
        rows,
        tags: (data.tags || []).map((tag) => ({
            id: toIntOrNull(tag.id),
            value: tag.value,
        })),
        payments: data.payments
            .filter(
                (payment) =>
                    !_.isEmpty(payment.date) &&
                    !_.isNil(payment.amount) &&
                    !_.isNil(payment.payment_mean),
            )
            .map((payment) => ({
                ...payment,
                date: payment.date.split(' ')[0],
                amount: parseAmount(payment.amount),
                payment_mean: toIntOrNull(payment.payment_mean),
            })),
        total_excl: parseAmount(data.totalWithoutTax),
        total_incl: parseAmount(data.totalWithTax),
    };
};

export const detectFormat = (value) => {
    const noSpaceValue = value.toString().replaceAll(' ', '');
    const commaIndex = noSpaceValue.indexOf(',');
    const dotIndex = noSpaceValue.indexOf('.');

    const format = { thousands: ' ' };

    const hasComma = commaIndex !== -1;
    const hasDot = dotIndex !== -1;

    if (hasComma && hasDot) {
        if (commaIndex > dotIndex) {
            format.decimals = ',';
            format.thousands = '.';
        } else {
            format.decimals = '.';
            format.thousands = ',';
        }
    } else if (hasComma) {
        format.decimals = ',';
    } else if (hasDot) {
        format.decimals = '.';
    }

    return format;
};
